import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import ChartLoadingWrapper from "@/components/ui/chart/ChartLoadingWrapper";
import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import type { ChartSeries } from "@/components/ui/chart/utils";
import Dropdown from "@/components/ui/dropdown";
import RetryErrorState from "@/components/ui/RetryErrorState";
import { GROUPED_CHART_COLORS } from "@/constants/colors";
import { MESSAGE } from "@/constants/message";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setGridsSelected } from "@/store/slice/analysisSummary";
import { WidgetType } from "@/types/widget";

import GeneralSummary from "./GeneralSummary";

type OverviewProps = {
  isExpanded: boolean;
  chartRef: React.RefObject<HTMLDivElement>;
  isFetching: boolean;
  isError: boolean;
  onRefetch?: () => void;
};

export default function Overview({
  isExpanded,
  chartRef,
  isFetching,
  isError,
  onRefetch
}: Readonly<OverviewProps>) {
  const dispatch = useDispatch();

  const {
    GRID_DATA,
    ATTRIBUTE_OPTIONS,
    handleAttributeChange,
    getTypeSummary
  } = useAnalysisSummaryDataContext();

  const { generalSummary, selectedAttribute, isHasSummary } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const { isDatasetWidgetEnabled } = useAppFeatures();

  const isWidgetLoading = isDatasetWidgetEnabled && isFetching;

  const handleOnBarClick = (name: string) => {
    dispatch(setGridsSelected([name]));
  };

  // Label the single measure so the chart legend shows the selected attribute
  // instead of the raw "value" data key.
  const chartSeries = useMemo<ChartSeries[]>(
    () => [
      {
        dataKey: "value",
        color: GROUPED_CHART_COLORS[0],
        label: selectedAttribute.value
      }
    ],
    [selectedAttribute.value]
  );

  if (isDatasetWidgetEnabled && isError && onRefetch) {
    return (
      <>
        <div>
          <h2 className="text-base font-semibold text-txtHeading">
            General Summary
          </h2>
        </div>
        <RetryErrorState
          onRetry={onRefetch}
          className="h-[600px]"
          title={
            MESSAGE.ANALYSIS_SUMMARY.GENERATING_SUMMARY_ERROR_WITH_RETRY_TITLE
          }
          description={
            MESSAGE.ANALYSIS_SUMMARY
              .GENERATING_SUMMARY_ERROR_WITH_RETRY_DESCRIPTION
          }
        />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <GeneralSummary
        quoteTxt={
          isExpanded
            ? `Overview of the spatial distribution of Point of Interest(POI) based
          on industry classifications, locations, and geographic spread.`
            : null
        }
        isFetching={isFetching}
      />

      {isHasSummary && (
        <div
          className="flex flex-row items-center justify-between px-3 py-2 mt-2 mb-4 font-semibold capitalize bg-white border border-gainsboro rounded-xl"
          role="region"
          aria-label={`Total ${getTypeSummary()} Summary`}
        >
          <p>Total {getTypeSummary()}</p>
          {isWidgetLoading ? (
            <p data-testid="total-grid-label">
              <div className={"rounded bg-whisper w-16 h-8"} />
            </p>
          ) : (
            <p data-testid="total-grid-label">
              {generalSummary?.data.general.total_rows}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col flex-1">
        <div className="flex flex-row items-center justify-between mb-3">
          <h1 className="text-sm font-semibold text-txtHeading">
            Top 10 {getTypeSummary()} by{" "}
            <span className="capitalize">{selectedAttribute.label}</span>
          </h1>

          <Dropdown
            className="max-w-[132px] w-full"
            name="attribute"
            placeholder="Select Attribute"
            data={ATTRIBUTE_OPTIONS}
            onSelect={handleAttributeChange}
            disabled={isWidgetLoading}
            value={selectedAttribute}
          />
        </div>
        {isWidgetLoading || GRID_DATA.length > 0 ? (
          <ChartLoadingWrapper
            isLoading={isWidgetLoading}
            chartType={WidgetType.BAR_H}
          >
            <HorizontalBarChart
              ref={chartRef}
              data={GRID_DATA}
              series={chartSeries}
              title={`Top 10 ${getTypeSummary()} by ${selectedAttribute.label}`}
              onBarClick={handleOnBarClick}
            />
          </ChartLoadingWrapper>
        ) : (
          !isFetching && (
            <div className="flex items-center justify-center flex-1 px-10 font-bold text-center text-txtHeading">
              No data available to display.
            </div>
          )
        )}
      </div>
    </div>
  );
}
